import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { supabase, PUBLIC_BASE_URL } from '@/lib/supabase';
import { useSession } from '@/hooks/useSession';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';

type Status =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; email: string }
  | { kind: 'error'; message: string };

// Magic-link login. The user enters an email; Supabase emails them a
// one-click link that redirects back to PUBLIC_BASE_URL/auth/callback (which
// detectSessionInUrl on the client picks up automatically / no callback
// route component needed, the URL hash is consumed on next App mount).
export default function Login() {
  const { ready, isAuthenticated } = useSession();
  const [searchParams] = useSearchParams();
  const reason = searchParams.get('reason');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  if (ready && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setStatus({ kind: 'sending' });
    const { error } = await supabase.auth.signInWithOtp({
      email: trimmed,
      options: {
        emailRedirectTo: `${PUBLIC_BASE_URL}/auth/callback`,
        // Terminal is invitation-only. Unknown emails get a silent reject from
        // Supabase ("Signups not allowed for otp"); we translate that to a
        // friendlier voice line. To onboard someone, an admin uses the
        // "Invite" button in the People page.
        shouldCreateUser: false,
      },
    });
    if (error) {
      // Supabase returns "Signups not allowed for otp" when the email isn't
      // already a registered user. Show a voice-aligned line instead of the
      // raw error.
      const friendly = /signups not allowed/i.test(error.message)
        ? 'Not on this frequency. Ask an admin.'
        : error.message;
      setStatus({ kind: 'error', message: friendly });
    } else {
      setStatus({ kind: 'sent', email: trimmed });
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-6">
      <div className="w-full max-w-sm space-y-8">
        {/* Brand */}
        <div className="space-y-3 text-center">
          <img src="/logo.png" alt="TAPES" className="mx-auto h-24 w-24 object-contain" />
          <div className="font-editorial text-display tracking-tight">TAPES</div>
          <div className="text-xs uppercase tracking-[0.3em] text-fg-faint">Terminal</div>
        </div>

        {reason === 'deactivated' ? (
          <div className="rounded-md border border-thermal-red/40 bg-thermal-red/5 p-3 text-sm text-thermal-red text-center">
            Channel closed. Your account is deactivated.
          </div>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Enter</CardTitle>
            <CardDescription>
              {status.kind === 'sent'
                ? `Sent. Tune to ${status.email}.`
                : 'Drop an address. The link finds you.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {status.kind === 'sent' ? (
              <div className="space-y-4">
                <p className="text-sm text-fg-muted">
                  Single-use. Expires in 60 minutes.
                </p>
                <Button variant="ghost" size="sm" onClick={() => setStatus({ kind: 'idle' })}>
                  Other address
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-3">
                <Input
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status.kind === 'sending'}
                />
                <Button type="submit" className="w-full" disabled={status.kind === 'sending'}>
                  {status.kind === 'sending' ? 'Transmitting…' : 'Transmit'}
                </Button>
                {status.kind === 'error' ? (
                  <p className="text-sm text-thermal-red">{status.message}</p>
                ) : null}
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-fg-faint">
          By invitation. Ask an admin.
        </p>
      </div>
    </div>
  );
}
